import { useAuth } from "contexts/AuthContext";
import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
} from "react";

const PermissionsContext = createContext(null);

export const PermissionsProvider = ({ children }) => {
  const { privileges = [] } = useAuth();

  // convert to a Set for fast lookup
  const privilegeSet = useMemo(() => new Set(privileges || []), [privileges]);

  const can = useCallback(
    (code) => {
      if (!code) return false;
      return privilegeSet.has(code);
    },
    [privilegeSet]
  );

  const canAny = useCallback(
    (codes = []) => {
      if (!Array.isArray(codes) || codes.length === 0) return false;
      return codes.some((c) => privilegeSet.has(c));
    },
    [privilegeSet]
  );

  const value = useMemo(
    () => ({
      privileges,
      can,
      canAny,
    }),
    [privileges, can, canAny]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissionsContext = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error(
      "usePermissionContext must be used within a PermissionProvider"
    );
  }
  return ctx;
};
